import React from 'react';
import { FormattedMessage, defineMessages } from 'react-intl-next';
import { useQuery } from 'react-apollo';
import { differenceInDays } from 'date-fns';

import Link from '@atlaskit/link';
import { Box, Flex, Inline, xcss, Stack, Text } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';
import Heading from '@atlaskit/heading';

import { DailyRefreshComponent } from '@confluence/admin-center/entry-points/DailyRefreshComponent';
import {
	Attribution,
	ErrorBoundary,
	ErrorDisplay,
	isUnauthorizedError,
} from '@confluence/error-boundary';
import { markErrorAsHandled } from '@confluence/graphql';

import { RecentActivitySectionQuery } from './RecentActivitySectionQuery.graphql';
import type { RecentActivitySectionQuery as RecentActivitySectionQueryType } from './__types__/RecentActivitySectionQuery';
import { RecentActivityChart } from './RecentActivityChart';

const i18n = defineMessages({
	sectionTitle: {
		id: 'company-hub.recent-activity-section.title',
		defaultMessage: 'Recent Activity',
		description: 'Title for the recent activity section on the hub dashboard page',
	},
	leaderboardTitle: {
		id: 'company-hub.recent-activity.leaderboard.title',
		defaultMessage: 'Most clicked items',
		description: 'Title for the most clicked items leaderboard on the hub dashboard page',
	},
	viewAnalyticsLink: {
		id: 'company-hub.recent-activity.view-analytics-link',
		defaultMessage: 'View Analytics',
		description: 'Link text to navigate to the analytics page',
	},
});

const cardMinHeight = '360px';

const leaderboardStyles = xcss({
	border: `1px solid ${token('color.border')}`,
	borderRadius: `${token('space.100')}`,
	backgroundColor: 'elevation.surface.overlay',
	minHeight: cardMinHeight,
	padding: 'space.300',
});

const RecentActivityLeaderboard = () => {
	return (
		<Box xcss={leaderboardStyles}>
			<Text>Leaderboard will go here</Text>
		</Box>
	);
};

const chartContainerStyles = xcss({
	flexGrow: 5,
	flexBasis: 0,
	minWidth: '415px',
});

const leaderboardContainerStyles = xcss({
	flexGrow: 3,
	flexBasis: 0,
	minWidth: '300px',
});

const responsiveStyles = xcss({
	minWidth: '250px',
	maxWidth: '1024px',
	display: 'flex',
	flexDirection: 'row',
	containerType: 'inline-size',
	containerName: 'RecentActivity',
});

const loadingStyles = xcss({
	width: '100%',
	minWidth: '250px',
	borderRadius: '6px',
	border: `1px solid ${token('color.border')}`,
	background: token('elevation.surface.overlay'),
	height: cardMinHeight,
});

const linkStyles = xcss({
	color: 'color.link',
});

type RecentActivitySectionProps = {
	spaceKey: string;
};

export const RecentActivitySection = ({ spaceKey }: RecentActivitySectionProps) => {
	const { data, loading, error } = useQuery<RecentActivitySectionQueryType>(
		RecentActivitySectionQuery,
		{
			variables: {
				key: spaceKey,
			},
			skip: !spaceKey,
		},
	);
	const spaceId = data?.space?.id;

	if (loading) {
		return <Box xcss={loadingStyles} testId="loading-state" />;
	}

	if (!!error) {
		if (isUnauthorizedError(error)) {
			markErrorAsHandled(error);
			return <Box testId="unauthorized-error-state" />;
		} else {
			return (
				<ErrorDisplay error={error}>
					<Box testId="generic-error-state" />
				</ErrorDisplay>
			);
		}
	}

	if (!spaceId) {
		return <Box testId="no-space-id-found" />;
	}

	const spaceCreatedDate = data?.space?.history?.createdDate
		? new Date(data?.space?.history?.createdDate)
		: undefined;
	const isSpaceCreatedDateOver1DayAgo = spaceCreatedDate
		? differenceInDays(new Date(), spaceCreatedDate) >= 1
		: undefined;

	return (
		<Stack space="space.200">
			<Inline alignBlock="center">
				<Heading as="h2" size="medium">
					<FormattedMessage {...i18n.sectionTitle} />
				</Heading>
			</Inline>
			<Inline alignBlock="stretch" spread="space-between">
				<DailyRefreshComponent fontSize="small" />
				<Box xcss={linkStyles}>
					<Link href="/wiki/company-hub/analytics" appearance="subtle">
						<FormattedMessage {...i18n.viewAnalyticsLink} />
					</Link>
				</Box>
			</Inline>
			<ErrorBoundary attribution={Attribution.COMPANY_HUB}>
				<Flex gap="space.200" xcss={responsiveStyles} wrap="wrap">
					<Box xcss={chartContainerStyles}>
						<RecentActivityChart
							spaceId={spaceId}
							isSpaceCreatedDateOver1DayAgo={isSpaceCreatedDateOver1DayAgo}
						/>
					</Box>
					<Box xcss={leaderboardContainerStyles}>
						<RecentActivityLeaderboard />
					</Box>
				</Flex>
			</ErrorBoundary>
		</Stack>
	);
};
